/**
 * Theme-aware colours for the report charts.
 *
 * WHY THIS FILE EXISTS. A Chart.js canvas cannot use a Tailwind utility or a CSS
 * custom property — it paints with whatever string you hand `fillStyle`. So the
 * chart colours had been hardcoded: the bars were `rgb(31, 147, 255)` (#1F93FF,
 * Chatwoot's blue) and the axis text fell through to Chart.js's own `#666`
 * default. Measured on the dark report card those read 5.23:1 for a blue that
 * isn't ours and 2.87:1 for axis labels that must be legible.
 *
 * The brand tokens live in `shared/assets/stylesheets/brand.scss` as bare
 * `L C H` triplets, and they FLIP between light and dark. Reading them at render
 * time — rather than freezing a hex at import time — is what lets one chart
 * config serve both themes.
 *
 * WHY THE 1x1 CANVAS. The tokens resolve to `oklch(...)`. Chart.js 2.9 hands
 * colour strings to its own `chartjs-color` parser, which predates OKLCH and
 * silently yields black for anything it cannot parse — a failure that looks
 * exactly like "the chart rendered". Painting the colour onto a 1x1 canvas and
 * reading the pixel back makes the BROWSER do the conversion, so we hand
 * Chart.js a plain `rgba()` it cannot misread.
 */

// Series colours come from the avatar family (`--avatar-1..5`), the closed set of
// five tones on the mint-to-sky arc that the dashboard avatars and the portal
// already use. Chart series therefore read as the same product as everything
// else, and — because those tokens flip per mode — stay legible on both grounds.
export const CHART_SERIES_TOKENS = [
  '--avatar-1',
  '--avatar-2',
  '--avatar-3',
  '--avatar-4',
  '--avatar-5',
];

export const CHART_FONT_FAMILY =
  'Outfit,"IBM Plex Sans Arabic",-apple-system,system-ui,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif';

/**
 * THE ELEMENT THE THEME TOKENS ACTUALLY LIVE ON.
 *
 * This was a real defect and it silently disabled dark mode for every chart in
 * the product. `brand.scss` declares the light palette on `:root` and the dark
 * palette on a BARE `.dark` class, and the four bundles put that class on four
 * different elements: the dashboard on `document.body`, the portal on
 * `document.documentElement`, and the widget and the login screen on a root
 * `<div>`. Reading the token off `document.documentElement` therefore returned
 * the LIGHT value on the dashboard even with dark mode active — measured in the
 * browser, `--avatar-1` came back as `0.5055 0.1095 162.59` (the light tone) on
 * a page whose body carried `dark`, where the body's own computed value was
 * `0.8691 0.1789 165.32`.
 *
 * The failure is invisible: the chart renders, in a colour from the palette,
 * just the wrong half of it — the trend series measured 2.99:1 against the dark
 * card, a hair under the 3:1 that WCAG 1.4.11 requires of a graphical object,
 * where the intended tone measures 11.9:1.
 *
 * Querying for the class finds whichever element is carrying it, in every
 * bundle, and falls back to the document element when nothing is — which is
 * exactly the light case.
 */
const getThemeHost = () => {
  if (typeof document === 'undefined') return null;
  return document.querySelector('.dark') || document.documentElement;
};

export const isDarkMode = () => {
  if (typeof document === 'undefined') return false;
  return Boolean(document.querySelector('.dark'));
};

let probeContext = null;

const getProbeContext = () => {
  if (probeContext) return probeContext;
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  probeContext = canvas.getContext('2d', { willReadFrequently: true });
  return probeContext;
};

/**
 * Resolve any CSS colour the browser understands — `oklch()` included — into an
 * `rgba()` string that Chart.js 2.9's parser accepts.
 *
 * @param {string} cssColor any CSS colour string
 * @param {string} fallback returned when there is no DOM or the value is invalid
 * @returns {string} an `rgba(r, g, b, a)` string
 */
export const resolveColor = (cssColor, fallback = 'rgba(0, 0, 0, 1)') => {
  const context = getProbeContext();
  if (!context || !cssColor) return fallback;
  try {
    context.clearRect(0, 0, 1, 1);
    // Seed with a known value: an unparseable assignment leaves `fillStyle`
    // untouched rather than throwing, so without this we could not tell the
    // difference between "resolved to black" and "was not understood".
    context.fillStyle = '#000000';
    context.fillStyle = cssColor;
    context.fillRect(0, 0, 1, 1);
    const [r, g, b, a] = context.getImageData(0, 0, 1, 1).data;
    return `rgba(${r}, ${g}, ${b}, ${(a / 255).toFixed(3)})`;
  } catch (error) {
    return fallback;
  }
};

/**
 * Read a brand token (stored as a bare `L C H` triplet) and return it resolved.
 *
 * @param {string} token custom property name, e.g. `--avatar-1`
 * @param {number} alpha 0..1
 * @param {string} fallback returned when the token is not defined
 */
export const resolveToken = (token, alpha = 1, fallback = null) => {
  if (typeof document === 'undefined') {
    return fallback || 'rgba(0, 0, 0, 1)';
  }
  const raw = getComputedStyle(getThemeHost()).getPropertyValue(token).trim();
  if (!raw) {
    return fallback || 'rgba(0, 0, 0, 1)';
  }
  return resolveColor(`oklch(${raw} / ${alpha})`, fallback || 'rgba(0,0,0,1)');
};

/**
 * Series colours for a chart, cycling the avatar family if a chart ever carries
 * more than five datasets.
 */
export const getChartSeriesColors = (count = 1) =>
  Array.from({ length: count }, (unused, index) =>
    resolveToken(CHART_SERIES_TOKENS[index % CHART_SERIES_TOKENS.length])
  );

/** The primary series colour — the brand accent, legible on the current ground. */
export const getPrimarySeriesColor = () => resolveToken('--avatar-1');

/**
 * Axis furniture. Chart.js 2.9 defaults to `#666` text and a near-black grid,
 * both of which were chosen for a white page.
 */
export const getAxisTheme = () => {
  const dark = isDarkMode();
  return {
    // slate-500 on light / slate-300 on dark: the muted-but-legible tier that
    // body copy elsewhere in the product uses against these same two grounds.
    tick: resolveToken(dark ? '--slate-300' : '--slate-500'),
    // The grid is structure, not content — it sits below the AA text threshold
    // on purpose, but must still be visible on BOTH grounds rather than being a
    // black line on near-black.
    grid: resolveToken(dark ? '--slate-100' : '--slate-200', dark ? 0.14 : 0.7),
    zeroLine: resolveToken(
      dark ? '--slate-100' : '--slate-300',
      dark ? 0.28 : 1
    ),
  };
};

/**
 * Tooltip styling. The Chart.js default is a black box with white text and its
 * own corner radius — a control that belongs to no design system.
 */
export const getTooltipTheme = (isRTL = false) => {
  const dark = isDarkMode();
  return {
    backgroundColor: resolveToken(dark ? '--slate-700' : '--slate-900', 0.96),
    titleFontColor: resolveToken(dark ? '--slate-25' : '--slate-25'),
    bodyFontColor: resolveToken(dark ? '--slate-100' : '--slate-100'),
    titleFontFamily: CHART_FONT_FAMILY,
    bodyFontFamily: CHART_FONT_FAMILY,
    titleFontSize: 12,
    bodyFontSize: 12,
    cornerRadius: 8,
    xPadding: 12,
    yPadding: 8,
    displayColors: false,
    borderColor: resolveToken(dark ? '--slate-500' : '--slate-700', 0.6),
    borderWidth: 1,
    // Chart.js 2.9 can mirror its own tooltip; without these the Arabic body
    // text is laid out left-to-right inside the box.
    rtl: isRTL,
    textDirection: isRTL ? 'rtl' : 'ltr',
  };
};

/**
 * Format a number for an axis tick or a tooltip in the app's locale, so Arabic
 * gets Arabic digit grouping instead of the browser's default.
 *
 * @param {number} value
 * @param {string} locale i18n locale, e.g. `ar` or `pt_BR`
 */
export const formatChartNumber = (value, locale = 'en') => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '';
  }
  try {
    return Number(value).toLocaleString(String(locale).replace('_', '-'));
  } catch (error) {
    return String(value);
  }
};

/**
 * Localised duration for the report metrics.
 *
 * `formatTime()` from `@chatwoot/utils` returns hardcoded ENGLISH units —
 * `formatTime(0)` is `"0 Sec"`, `formatTime(90)` is `"1 Min 30 Sec"`. Two things
 * go wrong with that in this product. The units are untranslatable, and the
 * digits and the Latin unit are bidi-opposed, so on an Arabic (RTL) line
 * `"0 Sec"` renders as `"Sec 0"` and `"1 Min 30 Sec"` renders as
 * `"Sec 30 Min 1"` — the parts come out in reverse order.
 *
 * `Intl` emits the unit in the active locale, which removes the mixed-direction
 * run that caused the reordering.
 *
 * @param {number} seconds
 * @param {string} locale i18n locale
 * @returns {string}
 */
export const formatDuration = (seconds, locale = 'en') => {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  const normalized = String(locale).replace('_', '-');

  const parts = [
    { unit: 'day', value: Math.floor(total / 86400) },
    { unit: 'hour', value: Math.floor((total % 86400) / 3600) },
    { unit: 'minute', value: Math.floor((total % 3600) / 60) },
    { unit: 'second', value: total % 60 },
  ];

  const format = (value, unit) => {
    try {
      return new Intl.NumberFormat(normalized, {
        style: 'unit',
        unit,
        unitDisplay: 'short',
      }).format(value);
    } catch (error) {
      // `style: 'unit'` is ES2020; fall back to a plain number plus a symbol.
      const symbols = { day: 'd', hour: 'h', minute: 'm', second: 's' };
      return `${value}${symbols[unit]}`;
    }
  };

  // Keep the two most significant non-zero components, which is the granularity
  // the previous helper used.
  const significant = parts.filter(part => part.value > 0).slice(0, 2);
  if (!significant.length) {
    return format(0, 'second');
  }
  return significant.map(part => format(part.value, part.unit)).join(' ');
};

/**
 * The card surface a chart is drawn on. Used to stroke a hairline between
 * touching segments of a stacked bar: adjacent fills are separated only by
 * their own colours, and any palette constrained to clear a contrast ratio
 * against BOTH grounds is squeezed into a narrow lightness band where
 * neighbouring hues inevitably collide.
 */
export const getSurfaceColor = () =>
  resolveToken(isDarkMode() ? '--slate-800' : '--slate-25', 1);

/**
 * THE RTL CHART DECISION, IN ONE PLACE.
 *
 * The evidence is genuinely split and the vendors contradict each other:
 * Datawrapper mirrors the time axis, AG Charts refuses to, Material Design
 * exempts charts from mirroring while mirroring everything else that happens
 * over time, and Chart.js 2.9's `rtl` support reaches the legend and the
 * tooltip but not the scales. The strongest evidence available is the IEEE VIS
 * 2024 corpus of 128 Arabic visualisations: 58% kept a numeric/time x-axis
 * running left-to-right, and 85% of that group were the mainstream outlets
 * (BBC Arabic, CNN Arabic) these managers read daily — while 81% put the
 * y-axis on the RIGHT when the x-axis was categorical, which the authors call
 * "a considerable consensus".
 *
 * The corpus's clearest negative finding is not any particular direction: it is
 * INCONSISTENCY WITHIN ONE ARTICLE, including outlets that flipped the y-axis
 * between two charts in the same piece. So the rule that matters more than the
 * choice is that the choice is made once, written down, and applied everywhere.
 * That is what this constant is for. Every chart in the reports area reads it;
 * nothing decides direction locally.
 */
export const RTL_CHART_POLICY = {
  /**
   * Time runs left-to-right in BOTH directions. uniLINK is a tool, not a news
   * graphic — its users cross-reference it against WhatsApp Manager, Meta
   * Business Suite and Excel, none of which reverse a time axis, so mirroring
   * would make uniLINK the odd one out inside a workflow. It is also the most
   * expensive option on Chart.js 2.9, which has no axis-reversal mode.
   */
  mirrorTimeAxis: false,
  /**
   * The value axis moves to the leading edge — the right in RTL. This is the
   * one clear consensus in the corpus and it costs nothing.
   */
  valueAxisOnLeadingEdge: true,
  /**
   * Ranking and proportion bars are categorical, not temporal: they mirror, so
   * they grow from the right edge with their labels on the right.
   */
  mirrorCategoryBars: true,
  /** Legend and tooltip mirror. Chart.js 2.9 supports both natively. */
  mirrorLegendAndTooltip: true,
};

/**
 * Line styling for the trend chart. Kept beside the axis theme so a series
 * colour and the grid it is drawn against are chosen against the same ground.
 */
export const getLineSeriesTheme = (index = 0) => {
  const color = getChartSeriesColors(index + 1)[index];
  return {
    borderColor: color,
    backgroundColor: color,
    pointBackgroundColor: color,
    pointBorderColor: getSurfaceColor(),
    pointHoverBackgroundColor: color,
    pointHoverBorderColor: getSurfaceColor(),
    borderWidth: 2,
    pointRadius: 2,
    pointHoverRadius: 4,
    fill: false,
    // Straight segments, not a spline. With a handful of buckets a tension of
    // 0.25 draws a smooth bell through 0, 10, 0 — a curve that asserts values
    // between the measurements that were never measured, and overshoots the
    // maximum on the way. The only points this product knows are the ones the
    // builder returned.
    lineTension: 0,
  };
};
